import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Text,
  View,
  TouchableOpacity,
  Alert,
  Share,
  Image,
  Modal,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import {useNavigation, RouteProp, useFocusEffect} from '@react-navigation/native';
import Clipboard from '@react-native-clipboard/clipboard';
import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';
import {styles} from './styles';
import {useUserStore} from '../../store/UserStore';
import {Colors} from '../../constants/Colors';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import {Images} from '../../constants/Images';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes, RootStackParamList} from '../../navigation/Routes.types';
import DebtsService from '../../services/DebtsService/DebtsService';

type DebtsPaymentPixRouteProp = RouteProp<
  RootStackParamList,
  AppRoutes.DEBTS_PAYMENT_PIX
>;

type PixDebtPaymentProps = {
  route: DebtsPaymentPixRouteProp;
};

const PixDebtPayment = ({route}: PixDebtPaymentProps) => {
  const {replace} = useNavigation<NavigationProps>();
  const {goBack} = useNavigation<NavigationProps>();
  const {user} = useUserStore();
  const {
    pix_qrcode_url,
    pix_copy_paste,
    amount,
    expiration_date,
    expected_on,
    reference_id,
  } = route.params.item;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const verifyIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Flag para controlar se o componente está ativo
  const isActiveRef = useRef(true);

  const FIXED_PIX_TIMER_SECONDS = 10 * 60; // 10 minutos fixo

  const calculateInitialSeconds = () => {
    return FIXED_PIX_TIMER_SECONDS;
  };

  const [secondsLeft, setSecondsLeft] = useState(calculateInitialSeconds());
  const [paymentApproved, setPaymentApproved] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [expiredModalVisible, setExpiredModalVisible] = useState(false);
  const [paymentConfirmationModalVisible, setPaymentConfirmationModalVisible] =
    useState(false);
  const [QRCodeUrl, setQRCodeUrl] = useState('');
  const [imageRetryCount, setImageRetryCount] = useState(0);
  const baseQRCodeUrlRef = useRef('');

  const MAX_IMAGE_RETRIES = 3;
  const RETRY_DELAY_MS = 2000;

  // ============================================
  // FUNÇÃO CENTRALIZADA DE CLEANUP
  // ============================================
  const clearAllTimers = useCallback(() => {
    console.log('[PixDebtPayment] Limpando todos os timers...');

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (verifyIntervalRef.current) {
      clearInterval(verifyIntervalRef.current);
      verifyIntervalRef.current = null;
    }

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    isActiveRef.current = false;
  }, []);

  // ============================================
  // useFocusEffect - LIMPA AO SAIR DA TELA
  // ============================================
  useFocusEffect(
    useCallback(() => {
      // Quando a tela ganha foco
      console.log('[PixDebtPayment] Tela em foco');
      isActiveRef.current = true;

      // Cleanup quando a tela perde foco (qualquer forma de saída)
      return () => {
        console.log('[PixDebtPayment] Tela perdeu foco - limpando timers');
        clearAllTimers();
      };
    }, [clearAllTimers]),
  );

  // ============================================
  // Cleanup adicional no unmount do componente
  // ============================================
  useEffect(() => {
    return () => {
      console.log('[PixDebtPayment] Componente desmontado - cleanup final');
      clearAllTimers();
    };
  }, [clearAllTimers]);

  const buildRetryUrl = useCallback((url: string, attempt: number) => {
    if (!url) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}retry=${attempt}_${Date.now()}`;
  }, []);

  useEffect(() => {
    if (!!pix_qrcode_url) {
      baseQRCodeUrlRef.current = pix_qrcode_url;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      setImageRetryCount(0);
      setQRCodeUrl(pix_qrcode_url);
    }
  }, [pix_qrcode_url]);

  const CheckPixStatus = useCallback(async () => {
    // Não executa se o componente não está ativo
    if (!isActiveRef.current) {
      return null;
    }

    const response = await DebtsService.CheckPixStatus({reference_id});
    return response;
  }, [reference_id]);

  const handleModalNavigate = useCallback(
    (routeName: string) => {
      // Limpa timers ANTES de navegar
      clearAllTimers();

      setPaymentConfirmationModalVisible(false);
      setPaymentApproved(false);
      setErrorModalVisible(false);
      setExpiredModalVisible(false);

      setTimeout(() => {
        replace(routeName);
      }, 400);
    },
    [replace, clearAllTimers],
  );

  // Handler para o goBack com cleanup
  const handleGoBack = useCallback(() => {
    clearAllTimers();
    goBack();
  }, [goBack, clearAllTimers]);

  useEffect(() => {
    setSecondsLeft(calculateInitialSeconds());
  }, [expected_on]);

  // ============================================
  // SETUP DOS INTERVALS COM VERIFICAÇÃO DE isActive
  // ============================================
  useEffect(() => {
    // Reseta flag ao iniciar
    isActiveRef.current = true;

    intervalRef.current = setInterval(() => {
      // Verifica se ainda está ativo antes de atualizar
      if (!isActiveRef.current) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearAllTimers();
          setExpiredModalVisible(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    verifyIntervalRef.current = setInterval(() => {
      // Verifica se ainda está ativo antes de fazer a chamada
      if (!isActiveRef.current) {
        if (verifyIntervalRef.current) {
          clearInterval(verifyIntervalRef.current);
          verifyIntervalRef.current = null;
        }
        return;
      }

      const check = async () => {
        try {
          const response = await CheckPixStatus();

          // Verifica novamente após a resposta assíncrona
          if (!isActiveRef.current) return;

          if (response?.status === 'succeeded') {
            clearAllTimers();
            setPaymentApproved(true);
          } else if (response?.status === 'failed') {
            clearAllTimers();
            setErrorModalVisible(true);
          }
        } catch (error) {
          console.warn('Erro ao verificar Pix:', error);
        }
      };

      check();
    }, 5000);

    return () => {
      clearAllTimers();
    };
  }, [pix_qrcode_url, CheckPixStatus, clearAllTimers]);

  const formatTime = (sec: number) => {
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const seconds = sec % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
        2,
        '0',
      )}:${String(seconds).padStart(2, '0')}`;
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0',
    )}`;
  };

  const copyToClipboard = (text: string) => {
    if (!text) {
      Alert.alert('Erro', 'Código PIX não disponível.');
      return;
    }
    Clipboard.setString(text);
    Alert.alert('Copiado!', 'Chave PIX copiada com sucesso.');
  };

  const onShareContent = async (content: string) => {
    if (!content) {
      Alert.alert('Erro', 'Código PIX não disponível.');
      return;
    }
    try {
      const result = await Share.share({message: content});
      if (result.action === Share.sharedAction) {
        Alert.alert('Compartilhado!', 'Código PIX compartilhado.');
      }
    } catch {
      Alert.alert('Erro', 'Não foi possível compartilhar.');
    }
  };

  // Função auxiliar para mostrar informações sobre o tempo de expiração
  const getExpirationInfo = () => {
    const expirationDate = new Date(
      Date.now() + FIXED_PIX_TIMER_SECONDS * 1000,
    );

    return {
      showExpectedTime: true,
      expectedTimeText: `Expira em: ${expirationDate.toLocaleDateString(
        'pt-BR',
      )} às ${expirationDate.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })}`,
    };
  };

  const expirationInfo = getExpirationInfo();

  return (
    <View style={styles.container}>
      <Header />
      <ScrollView
        contentContainerStyle={{flexGrow: 1, paddingBottom: 40}}
        showsVerticalScrollIndicator={false}>
        <View style={styles.spaceHeader}>
          <SearchHeader
            onPress={handleGoBack}
            title="Pagamento com Pix"
            mb={20}
          />
        </View>
        <TemporaryPasswordBanner />

        <View style={{justifyContent: 'center', alignItems: 'center'}}>
          {!!QRCodeUrl ? (
            <Image
              source={{uri: QRCodeUrl}}
              style={styles.imageStyle}
              resizeMode="contain"
              onLoad={() => {
                if (retryTimeoutRef.current) {
                  clearTimeout(retryTimeoutRef.current);
                  retryTimeoutRef.current = null;
                }

                if (imageRetryCount > 0) {
                  console.log(
                    '[PixDebtPayment] QR code image loaded after retries:',
                    imageRetryCount,
                  );
                }

                setImageRetryCount(0);
              }}
              onError={event => {
                console.log('[PixDebtPayment] QR code image failed to load', {
                  error: event?.nativeEvent,
                  uri: QRCodeUrl,
                  retryAttempt: imageRetryCount,
                });

                if (retryTimeoutRef.current) {
                  clearTimeout(retryTimeoutRef.current);
                  retryTimeoutRef.current = null;
                }

                // Verifica se ainda está ativo antes de fazer retry
                if (!isActiveRef.current) return;

                if (imageRetryCount < MAX_IMAGE_RETRIES) {
                  const nextAttempt = imageRetryCount + 1;
                  const baseUrl = baseQRCodeUrlRef.current || QRCodeUrl;

                  console.log(
                    `[PixDebtPayment] Scheduling retry ${nextAttempt} for QR code image`,
                  );

                  setImageRetryCount(nextAttempt);

                  retryTimeoutRef.current = setTimeout(() => {
                    // Verifica novamente antes de atualizar o state
                    if (!isActiveRef.current) return;

                    const retryingUrl = buildRetryUrl(baseUrl, nextAttempt);
                    console.log(
                      `[PixDebtPayment] Retrying QR code image load (attempt ${nextAttempt}) with URL: ${retryingUrl}`,
                    );
                    setQRCodeUrl(retryingUrl);
                  }, RETRY_DELAY_MS);
                } else {
                  console.log(
                    '[PixDebtPayment] Max retries reached for QR code image load',
                  );
                }
              }}
            />
          ) : (
            <ActivityIndicator size="large" color={Colors.RedPallete.red} />
          )}

          <Text
            style={{
              fontSize: 11,
              color: 'black',
              fontFamily: 'TCCC-UnityText Bold',
              marginTop: 20,
            }}>
            Clique para copiar a chave PIX
          </Text>

          <TouchableOpacity
            style={{marginTop: 20}}
            onPress={() => copyToClipboard(pix_copy_paste)}>
            <Text
              style={{
                color: Colors.GrayPallete.lightgrayThree,
                fontSize: 12,
                fontFamily: 'TCCC-UnityHeadline Regular',
                textAlign: 'center',
              }}>
              {pix_copy_paste || 'Código não disponível'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity onPress={() => onShareContent(pix_copy_paste)}>
            <Text
              style={{
                color: Colors.RedPallete.red,
                fontSize: 14,
                fontFamily: 'TCCC-UnityHeadline Regular',
                marginTop: 20,
              }}>
              Compartilhar
            </Text>
          </TouchableOpacity>

          <View style={{marginTop: 30, alignItems: 'center', width: '80%'}}>
            <Text
              style={{
                fontSize: 22,
                lineHeight: 26,
                color: 'black',
                fontWeight: '700',
                marginBottom: 8,
              }}>
              R$ {amount?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
            </Text>

            <Text
              style={{
                fontSize: 14,
                fontWeight: '400',
                color: Colors.GrayPallete.lightgrayThree,
                marginBottom: 20,
              }}>
              Vencimento:{' '}
              {new Date(expiration_date).toLocaleDateString('pt-BR', {
                timeZone: 'America/Sao_Paulo',
              }) ?? '---'}
            </Text>

            <Text
              style={{
                fontSize: 12,
                fontWeight: '600',
                marginBottom: 8,
                color: Colors.GrayPallete.dark,
              }}>
              PIX válido por:
            </Text>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 12,
              }}>
              <Image
                source={Images.hourGlassIcon}
                style={{
                  width: 11,
                  height: 18,
                  tintColor: Colors.RedPallete.red,
                  marginRight: 6,
                }}
              />
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: Colors.RedPallete.red,
                }}>
                {formatTime(secondsLeft)}
              </Text>
            </View>

            {expirationInfo.showExpectedTime && (
              <Text
                style={{
                  fontSize: 10,
                  color: Colors.GrayPallete.lightgrayThree,
                  textAlign: 'center',
                  marginTop: 4,
                }}>
                {expirationInfo.expectedTimeText}
              </Text>
            )}
          </View>

          <View style={{marginTop: 40, paddingHorizontal: 20}}>
            <ButtonComponent
              title="Já realizei o pagamento"
              width="100%"
              onPress={() => setPaymentConfirmationModalVisible(true)}
            />
          </View>
        </View>
      </ScrollView>

      {/* MODAL DE CONFIRMAÇÃO DE PAGAMENTO */}
      <Modal
        transparent={true}
        visible={paymentConfirmationModalVisible}
        animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            {/* Ícone de informação */}
            <View
              style={[
                styles.modalIconContainer,
                {backgroundColor: Colors.BluePallete?.blue || '#0066CC'},
              ]}>
              <Text style={styles.modalIcon}>ℹ</Text>
            </View>

            {/* Título */}
            <Text
              style={[
                styles.modalTitle,
                {color: Colors.BluePallete?.blue || '#0066CC'},
              ]}>
              Pagamento em Processamento
            </Text>

            {/* Descrição */}
            <Text style={styles.modalDescription}>
              Assim que o pagamento for identificado, sua dívida será quitada
              automaticamente.
              {'\n\n'}
              Você pode acompanhar o status na página de{' '}
              <Text style={{fontWeight: 'bold'}}>Minhas Dívidas</Text>.
            </Text>

            {/* Botão */}
            <ButtonComponent
              title="Ir para Minhas Dívidas"
              width="100%"
              onPress={() => handleModalNavigate(AppRoutes.DEBTS)}
            />
          </View>
        </View>
      </Modal>

      {/* Modal de Pagamento Aprovado */}
      <Modal transparent={true} visible={paymentApproved} animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            {/* Ícone de sucesso */}
            <View
              style={[
                styles.modalIconContainer,
                styles.modalIconContainerSuccess,
              ]}>
              <Text style={styles.modalIcon}>✓</Text>
            </View>

            {/* Título */}
            <Text style={[styles.modalTitle, styles.modalTitleSuccess]}>
              Pagamento Aprovado!
            </Text>

            {/* Subtítulo */}
            <Text style={styles.modalSubtitle}>
              Transação realizada com sucesso
            </Text>

            {/* Descrição */}
            <Text style={styles.modalDescription}>
              Em breve seu crédito será liberado!
            </Text>

            {/* Botão */}
            <ButtonComponent
              title="Continuar"
              width="100%"
              onPress={() => handleModalNavigate(AppRoutes.DEBTS)}
            />
          </View>
        </View>
      </Modal>

      {/* Modal de Erro */}
      <Modal
        transparent={true}
        visible={errorModalVisible}
        animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            {/* Ícone de erro */}
            <View
              style={[
                styles.modalIconContainer,
                styles.modalIconContainerError,
              ]}>
              <Text style={styles.modalIcon}>⚠</Text>
            </View>

            {/* Título */}
            <Text style={[styles.modalTitle, styles.modalTitleError]}>
              Erro no Pagamento
            </Text>

            {/* Descrição */}
            <Text style={styles.modalDescription}>
              Não foi possível processar o pagamento. Verifique seus dados ou
              tente novamente mais tarde.
            </Text>

            {/* Botão */}
            <ButtonComponent
              title="Tentar Novamente"
              width="100%"
              onPress={() => handleModalNavigate(AppRoutes.DEBTS)}
            />
          </View>
        </View>
      </Modal>

      {/* Modal de Tempo Expirado */}
      <Modal
        transparent={true}
        visible={expiredModalVisible}
        animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            {/* Ícone de tempo expirado */}
            <View
              style={[
                styles.modalIconContainer,
                styles.modalIconContainerExpired,
              ]}>
              <Text style={styles.modalIconExpired}>⏰</Text>
            </View>

            {/* Título */}
            <Text style={[styles.modalTitle, styles.modalTitleExpired]}>
              Tempo Expirado
            </Text>

            {/* Subtítulo */}
            <Text style={styles.modalSubtitle}>
              O tempo para pagamento expirou
            </Text>

            {/* Descrição */}
            <Text style={styles.modalDescription}>
              Se você já realizou o pagamento, aguarde o processamento. Caso
              contrário, inicie um novo processo de pagamento.
            </Text>

            {/* Botão */}
            <ButtonComponent
              title="Entendido"
              width="100%"
              onPress={() => handleModalNavigate(AppRoutes.DEBTS)}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default PixDebtPayment;